/**
 * Minimal typed fetch wrapper for the LogFetcher API.
 *
 * Authentication is HTTP Basic Auth handled by the browser: the first request
 * to a protected endpoint returns `401 WWW-Authenticate`, the browser prompts
 * for credentials, and subsequently sends them automatically. We therefore do
 * not manage credentials in JS — we only set `credentials: 'include'` so the
 * browser attaches them.
 */

/** Error thrown for non-2xx API responses, carrying the HTTP status. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Performs a JSON API request and returns the parsed body.
 *
 * @param path - API path beginning with `/api`.
 * @param init - Optional `fetch` init overrides.
 * @returns The parsed JSON response.
 * @throws {ApiError} On any non-2xx response.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });

  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = await response.json();
      message = body.message ?? message;
    } catch {
      // Response had no JSON body; keep the status text.
    }
    throw new ApiError(response.status, message);
  }

  // 204 responses have no body.
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Builds the download URL for a stored log file. */
export function downloadUrl(deviceHash: string, requestId: string, filename: string): string {
  return `/api/logs/${encodeURIComponent(deviceHash)}/${encodeURIComponent(
    requestId,
  )}/${encodeURIComponent(filename)}`;
}
